import { type IndexUiState } from 'instantsearch.js';

import { type RequestContext } from '~/types/RequestContext';

import { BettingInsightsComponent } from '../BettingInsightsConfiguration';
import {
	type BettingInsightsAggregatedResultAttribute,
	type BettingInsightsContent,
	type BettingInsightsResult,
	type EsIndex,
} from '../types';

import {
	BettingInsightsCardTitle,
	type BettingInsightsCardTitleConfig,
} from './BettingInsightsCardTitle';
import {
	BettingInsightsFilter,
	type BettingInsightsFilterConfiguration,
} from './BettingInsightsFilter';
import {
	BettingInsightsSorting,
	type BettingInsightsSortingConfig,
} from './BettingInsightsSorting';
import {
	BettingInsightsTable,
	type BettingInsightsTableConfig,
} from './BettingInsightsTable';

export interface BettingInsightsUIConfig<
	TIndex extends EsIndex,
	TResult = BettingInsightsResult<TIndex>,
	TContent = BettingInsightsContent<TIndex>,
> {
	card: {
		table: BettingInsightsTableConfig<TResult, TContent>;
		title?: BettingInsightsCardTitleConfig<TResult>;
	};
	initialUiState?: (
		this: BettingInsightsUI<TIndex, TResult, TContent>,
	) => IndexUiState;
	refinements: BettingInsightsFilterConfiguration<TIndex>[];
	results: {
		categories: {
			attribute: BettingInsightsAggregatedResultAttribute<TIndex>;
			order: string[];
		};
		labelledBy: string;
		propsName: string;
	};
	sorting: BettingInsightsSortingConfig<TIndex>;
}

export class BettingInsightsUI<
		TIndex extends EsIndex,
		TResult = BettingInsightsResult<TIndex>,
		TContent = BettingInsightsContent<TIndex>,
	>
	extends BettingInsightsComponent
	implements BettingInsightsUIConfig<TIndex, TResult, TContent>
{
	card: {
		table: BettingInsightsTable<TResult, TContent>;
		title?: BettingInsightsCardTitle<TResult>;
	};
	initialUiState?: () => IndexUiState;
	refinements: BettingInsightsFilter<TIndex>[];
	results: {
		categories: {
			attribute: BettingInsightsAggregatedResultAttribute<TIndex>;
			order: string[];
		};
		labelledBy: string;
		propsName: string;
	};
	sorting: BettingInsightsSorting<TIndex>;

	constructor({
		card,
		initialUiState,
		refinements,
		results,
		sorting,
		...requestContext
	}: BettingInsightsUIConfig<TIndex, TResult, TContent> & RequestContext) {
		super(requestContext);
		this.card = {
			table: BettingInsightsTable.withRequestContext<TResult, TContent>(
				requestContext,
				card.table,
			),
			title: card.title
				? BettingInsightsCardTitle.withRequestContext<TResult>(
						requestContext,
						card.title,
					)
				: undefined,
		};
		this.initialUiState = initialUiState?.bind(this);
		this.refinements = refinements.map(filter =>
			BettingInsightsFilter.withRequestContext<TIndex>(requestContext, filter),
		);
		this.results = results;
		this.sorting = BettingInsightsSorting.withRequestContext<TIndex>(
			requestContext,
			sorting,
		);
	}

	static withRequestContext<TIndex extends EsIndex>(
		requestContext: RequestContext,
		config: BettingInsightsUIConfig<TIndex>,
	) {
		return new this<TIndex>({
			...requestContext,
			...config,
		});
	}
}
